import {
  Add as AddIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as ActivateIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { TableSkeleton } from '../../../../components/TableSkeleton';
import CustomTextField from '../../../../components/Textfield';
import useCenterUsers from '../../../../hooks/useCenterUsers';

const CenterUserManagement = () => {
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [toggleUser, setToggleUser] = useState(null); // { id, isActive } of user being toggled
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    centerUsers,
    isCenterUsersLoading,
    totalCenterUsers,
    addCenterUser,
    isAddingCenterUser,
    deleteCenterUser,
    isDeletingCenterUser,
    toggleCenterUserStatus,
    isTogglingStatus,
  } = useCenterUsers(page + 1, rowsPerPage);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const handleOpenDialog = () => {
    reset({ firstName: '', lastName: '', email: '', phoneNumber: '' });
    setOpen(true);
  };

  const handleCloseDialog = () => setOpen(false);

  const handleSave = async (values) => {
    try {
      await addCenterUser(values);
      toast.success('Center user added. Invitation email sent.');
      handleCloseDialog();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCenterUser(deleteUserId);
      toast.success('User deleted');
      setDeleteUserId(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong');
    }
  };

  const handleToggleStatus = async () => {
    try {
      await toggleCenterUserStatus(toggleUser.id);
      toast.success(`Center user ${toggleUser.isActive ? 'deactivated' : 'activated'} successfully`);
      setToggleUser(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong');
    }
  };

  if (isCenterUsersLoading) return <TableSkeleton />;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Center Users
        </Typography>
        <Button
          variant="contained"
          sx={{ background: '#454751d1', fontSize: 15 }}
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add User
        </Button>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ width: '100%', overflow: 'visible' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontSize: '14px', fontWeight: 'bold' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Center Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {centerUsers?.length > 0 ? (
                centerUsers.map((user) => (
                  <TableRow key={user._id} sx={{ '& td': { fontSize: '14px' } }}>
                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phoneNumber || '—'}</TableCell>
                    <TableCell>{user.centerName || '—'}</TableCell>
                    <TableCell>
                      {!user.isPasswordSet ? (
                        <Chip label="Invite Pending" color="warning" size="small" variant="outlined" sx={{ fontSize: '13px' }} />
                      ) : user.isActive ? (
                        <Chip label="Active" color="success" size="small" variant="outlined" sx={{ fontSize: '13px' }} />
                      ) : (
                        <Chip label="Inactive" color="error" size="small" variant="outlined" sx={{ fontSize: '13px' }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {user.isPasswordSet && (
                        <Tooltip
                          title={user.isActive ? 'Deactivate Center' : 'Activate Center'}
                          componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}
                        >
                          <IconButton
                            color={user.isActive ? 'warning' : 'success'}
                            onClick={() => setToggleUser({ id: user._id, isActive: user.isActive })}
                            size="small"
                          >
                            {user.isActive
                              ? <BlockIcon fontSize="large" />
                              : <ActivateIcon fontSize="large" />
                            }
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        color="error"
                        onClick={() => setDeleteUserId(user._id)}
                        size="small"
                      >
                        <DeleteIcon fontSize="large" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="h6">No center users found. Add a user to get started.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCenterUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 0.5,
            '& .MuiTablePagination-toolbar': { minHeight: 52, flexWrap: 'wrap', gap: 1 },
            '& .MuiTablePagination-selectLabel': { fontSize: '14px', fontWeight: 500, m: 0 },
            '& .MuiTablePagination-displayedRows': { fontSize: '14px', fontWeight: 500, m: 0 },
            '& .MuiTablePagination-select': { fontSize: '14px', fontWeight: 500 },
            '& .MuiTablePagination-actions .MuiIconButton-root': {
              p: '6px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              mx: 0.3,
              '& svg': { fontSize: '18px' },
            },
          }}
        />
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit(handleSave)}>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              Add Center User
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <CustomTextField
                  {...register('firstName', { required: 'First name is required' })}
                  fullWidth
                  label="First Name*"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <CustomTextField
                  {...register('lastName', { required: 'Last name is required' })}
                  fullWidth
                  label="Last Name*"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <CustomTextField
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' },
                  })}
                  fullWidth
                  label="Email*"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <CustomTextField
                  {...register('phoneNumber')}
                  fullWidth
                  label="Phone Number"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ fontSize: 14, color: '#454751d1' }}>
              Cancel
            </Button>
            <Button
              sx={{ fontSize: 14, background: '#454751d1' }}
              type="submit"
              variant="contained"
              disabled={isAddingCenterUser}
            >
              {isAddingCenterUser ? 'Sending...' : 'Add & Send Invite'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Toggle Status Confirmation Dialog */}
      <Dialog open={!!toggleUser} onClose={() => setToggleUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {toggleUser?.isActive ? 'Deactivate Center' : 'Activate Center'}
            <IconButton edge="end" color="inherit" onClick={() => setToggleUser(null)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 3 }}>
          <Typography fontSize={14}>
            {toggleUser?.isActive
              ? 'Are you sure you want to deactivate this center? The center user will no longer be able to log in.'
              : 'Are you sure you want to activate this center? The center user will be able to log in again.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleUser(null)} sx={{ fontSize: 13, color: '#454751d1' }}>
            Cancel
          </Button>
          <Button
            onClick={handleToggleStatus}
            sx={{ fontSize: 13 }}
            variant="contained"
            disabled={isTogglingStatus}
            color={toggleUser?.isActive ? 'warning' : 'success'}
          >
            {toggleUser?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteUserId} onClose={() => setDeleteUserId(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Delete User
            <IconButton edge="end" color="inherit" onClick={() => setDeleteUserId(null)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 3 }}>
          Are you sure you want to delete this center user?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserId(null)} sx={{ fontSize: 13, color: '#454751d1' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            sx={{ fontSize: 13 }}
            variant="contained"
            disabled={isDeletingCenterUser}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CenterUserManagement;
